/**
 * Export utility — generates CSV or Excel-compatible HTML downloads.
 * No external library required.
 *
 * - CSV:   UTF-8 BOM prefix so Excel opens with correct encoding.
 * - Excel: Styled HTML table saved as .xls — Excel / LibreOffice opens it natively
 *          with full formatting (header colours, borders, footer row).
 */

import { formatDateDMY } from './date';

export type ExportFormat = 'csv' | 'excel' | 'pdf';

export interface ExportColumn {
  label:   string;
  key:     string;
  /** Optional formatter; receives raw cell value, returns string to emit. */
  format?: (value: unknown) => string;
  /** Optional inline CSS for the cell (PDF export only); receives raw cell value. */
  cellStyle?: (value: unknown) => string;
  /** Identifying column (code / head / anbiyam) — stays visible on an inactive family's row. */
  identity?: boolean;
}

export interface ExportConfig {
  title:    string;       // Sheet / document title
  filename: string;       // Base filename without extension
  columns:  ExportColumn[];
  rows:     Record<string, unknown>[];
  filters?: Record<string, string>; // human-readable active filters to show in footer
}

/** One titled table within a multi-section report (see exportSections). */
export interface ExportSection {
  title:   string;
  columns: ExportColumn[];
  rows:    Record<string, unknown>[];
}

export interface ExportSectionsConfig {
  title:    string;              // Report title
  filename: string;              // Base filename without extension
  sections: ExportSection[];     // Rendered one after another, each with its own heading
  filters?: Record<string, string>;
}

// ── Helpers ──────────────────────────────────────────────────────────────────

const cell = (col: ExportColumn, row: Record<string, unknown>): string => {
  const raw = row[col.key] ?? '';
  return col.format ? col.format(raw) : String(raw ?? '');
};

// ── Inactive families ────────────────────────────────────────────────────────
// A row with isActive === false (family marked inactive: left / moved /
// duplicate) is printed greyed out with an INACTIVE badge, and every column
// after the identifying ones collapses into one striped "no action needed"
// cell. CSV/Excel get a Status column instead. Mirrors
// backend/shared/pdfTemplate.js so bulk-export PDFs look the same.
type Row = Record<string, unknown>;
const isInactiveRow = (row: Row) => row.isActive === false;
const identityCount = (columns: ExportColumn[]) =>
  columns.filter((c) => c.identity).length || Math.min(2, columns.length);
const INACTIVE_BADGE = '<span style="display:inline-block;background:#b91c1c;color:#fff;font-weight:bold;font-size:9px;letter-spacing:.6px;padding:1px 6px;border-radius:3px;margin-left:6px;vertical-align:middle">INACTIVE</span>';

function pdfRow(columns: ExportColumn[], row: Row, i: number): string {
  if (isInactiveRow(row)) {
    const n = identityCount(columns);
    const idCells = columns.slice(0, n).map((col, k) =>
      `<td style="border:1px solid #9ca3af;padding:5px 9px;background:#e5e7eb;color:#4b5563;font-size:11px;">${cell(col, row)}${k === n - 1 ? INACTIVE_BADGE : ''}</td>`,
    ).join('');
    const rest = columns.length - n;
    const msg = rest > 0
      ? `<td colspan="${rest}" style="border:1px solid #9ca3af;padding:5px 9px;font-size:11px;font-weight:bold;color:#7f1d1d;text-align:center;letter-spacing:.3px;background:repeating-linear-gradient(135deg,#f3f4f6 0,#f3f4f6 6px,#e5e7eb 6px,#e5e7eb 12px);">INACTIVE FAMILY &mdash; no collection / no action needed</td>`
      : '';
    return `<tr>${idCells}${msg}</tr>`;
  }
  const bg = i % 2 === 0 ? '#ffffff' : '#f8fafc';
  const cells = columns
    .map((col) => {
      const style = col.cellStyle ? col.cellStyle(row[col.key]) : '';
      return `<td style="border:1px solid #d1d5db;padding:5px 9px;background:${bg};font-size:11px;${style}">${cell(col, row)}</td>`;
    })
    .join('');
  return `<tr>${cells}</tr>`;
}

const inactiveFootnote = (rows: Row[]) => {
  const n = rows.filter(isInactiveRow).length;
  return n ? ` &nbsp;|&nbsp; Inactive families: <b>${n}</b> (marked &mdash; no action needed)` : '';
};

/** CSV/Excel: add a Status column (after the identifying columns) when rows carry isActive. */
function withStatusColumn(columns: ExportColumn[], rows: Row[]): ExportColumn[] {
  if (!rows.some((r) => typeof r.isActive === 'boolean')) return columns;
  const n = identityCount(columns);
  const status: ExportColumn = {
    label: 'Family Status', key: 'isActive',
    format: (v) => (v === false ? 'INACTIVE - no action needed' : 'Active'),
  };
  return [...columns.slice(0, n), status, ...columns.slice(n)];
}

const escapeCsv = (v: string): string => {
  if (/[",\r\n]/.test(v)) return `"${v.replace(/"/g, '""')}"`;
  return v;
};

// Indian date convention (dd/mm/yyyy) — matches on-screen display everywhere else.
const fmtDate = (v: unknown): string => {
  if (!v) return '';
  const formatted = formatDateDMY(v, '');
  return formatted || String(v);
};

// ── CSV ───────────────────────────────────────────────────────────────────────

function buildCsv(config: ExportConfig): string {
  const { title, columns, rows, filters } = config;
  const lines: string[] = [];

  // Title
  lines.push(escapeCsv(title));

  // Active filters line
  if (filters && Object.keys(filters).length > 0) {
    const filterStr = Object.entries(filters)
      .filter(([, v]) => v)
      .map(([k, v]) => `${k}: ${v}`)
      .join(' | ');
    if (filterStr) lines.push(escapeCsv(`Filters — ${filterStr}`));
  }

  lines.push(''); // blank separator

  // Header
  lines.push(columns.map((c) => escapeCsv(c.label)).join(','));

  // Data
  rows.forEach((row) =>
    lines.push(columns.map((col) => escapeCsv(cell(col, row))).join(','))
  );

  lines.push(''); // blank row
  lines.push(escapeCsv(`Total records: ${rows.length}`));
  lines.push(escapeCsv(`Generated: ${new Date().toLocaleString()}`));
  lines.push(escapeCsv('Fatima Church Management System — Our Lady of Fatima, Tambaram'));

  return lines.join('\r\n');
}

// ── Excel (styled HTML) ───────────────────────────────────────────────────────

function buildExcel(config: ExportConfig): string {
  const { title, columns, rows, filters } = config;

  const filterRows = filters
    ? Object.entries(filters)
        .filter(([, v]) => v)
        .map(([k, v]) => `<tr><td colspan="${columns.length}" style="color:#4A7C9C;font-size:11px;padding:2px 8px;">${k}: <b>${v}</b></td></tr>`)
        .join('')
    : '';

  const dataRows = rows.map((row, i) => {
    const bg = i % 2 === 0 ? '#ffffff' : '#f5f7fa';
    const cells = columns.map((col) =>
      `<td style="border:1px solid #d1d5db;padding:6px 10px;background:${bg};">${cell(col, row)}</td>`
    ).join('');
    return `<tr>${cells}</tr>`;
  }).join('');

  const now = new Date().toLocaleString();

  return `<?xml version="1.0" encoding="UTF-8"?>
<html xmlns:o="urn:schemas-microsoft-com:office:office"
      xmlns:x="urn:schemas-microsoft-com:office:excel"
      xmlns="http://www.w3.org/TR/REC-html40">
<head>
  <meta charset="UTF-8"/>
  <!--[if gte mso 9]><xml><x:ExcelWorkbook><x:ExcelWorksheets>
    <x:ExcelWorksheet><x:Name>${title}</x:Name>
    <x:WorksheetOptions><x:DisplayGridlines/></x:WorksheetOptions>
    </x:ExcelWorksheet></x:ExcelWorksheets></x:ExcelWorkbook></xml><![endif]-->
  <style>
    body  { font-family: Arial, sans-serif; font-size: 12px; }
    table { border-collapse: collapse; width: 100%; }
    .church-header { background:#1B2A4A; color:#D4AF37; font-size:18px; font-weight:bold; padding:12px 16px; }
    .sub-header    { background:#1B2A4A; color:#93c5fd; font-size:11px; padding:4px 16px 10px; }
    .col-header    { background:#2E5C8A; color:#ffffff; font-weight:bold; padding:8px 12px;
                     border:1px solid #1B2A4A; font-size:11px; white-space:nowrap; }
    .footer        { background:#f0f4f8; color:#6b7280; font-size:10px; padding:6px 10px;
                     border-top:2px solid #2E5C8A; }
  </style>
</head>
<body>
<table>
  <tr><td colspan="${columns.length}" class="church-header">✝ ${title}</td></tr>
  <tr><td colspan="${columns.length}" class="sub-header">Our Lady of Fatima, Tambaram — Church Management System</td></tr>
  ${filterRows ? `<tr><td colspan="${columns.length}" style="background:#eff6ff;padding:4px 8px;font-size:11px;color:#1e40af;border-bottom:1px solid #bfdbfe;">Active filters: ${
    Object.entries(filters ?? {}).filter(([,v])=>v).map(([k,v])=>`${k}: ${v}`).join(' | ')
  }</td></tr>` : ''}
  <tr>${columns.map((c) => `<td class="col-header">${c.label}</td>`).join('')}</tr>
  ${dataRows}
  <tr>
    <td colspan="${columns.length}" class="footer">
      Total records: <b>${rows.length}</b> &nbsp;|&nbsp; Generated: ${now}
    </td>
  </tr>
</table>
</body>
</html>`;
}

// ── PDF (print-in-new-window) ─────────────────────────────────────────────────

function buildPdf(config: ExportConfig): string {
  const { title, columns, rows, filters } = config;
  const now = new Date().toLocaleString();
  const logoUrl = `${window.location.origin}/images/mary-fatima.png`;

  const filterStr = filters
    ? Object.entries(filters).filter(([, v]) => v).map(([k, v]) => `${k}: ${v}`).join(' | ')
    : '';

  const dataRows = rows.map((row, i) => pdfRow(columns, row, i)).join('');

  return `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8"/>
  <title>${title}</title>
  <style>
    @page { margin: 1.2cm; }
    @media print { .no-print { display:none !important; } }
    * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    body    { font-family: Georgia, "Times New Roman", serif; margin: 0; padding: 18px; color: #1f2937; }
    table   { border-collapse: collapse; width: 100%; margin-top: 14px; }
    .letterhead   { text-align: center; padding-bottom: 10px; border-bottom: 2px solid #1B2A4A; margin-bottom: 14px; }
    .letterhead img { height: 64px; margin-bottom: 6px; }
    .shrine-name  { font-size: 19px; font-weight: bold; letter-spacing: 0.5px; color: #1B2A4A; }
    .shrine-addr  { font-size: 11px; color: #4b5563; margin-top: 3px; }
    .report-title { font-size: 15px; font-weight: bold; color: #1B2A4A; margin-top: 10px; }
    .report-filters { font-size: 10.5px; color: #6b7280; margin-top: 2px; font-weight: normal; }
    th      { background:#2E5C8A; color:#fff; padding:7px 10px; border:1px solid #1B2A4A; font-size:11px; text-align:left; white-space:nowrap; font-family: Arial, sans-serif; }
    td      { font-family: Arial, sans-serif; }
    .foot   { margin-top:10px; font-size:10px; color:#6b7280; border-top:2px solid #2E5C8A; padding-top:6px; font-family: Arial, sans-serif; }
    .pbtn   { position:fixed; top:12px; right:12px; background:#2E5C8A; color:#fff; border:none;
              padding:8px 18px; cursor:pointer; font-size:13px; border-radius:4px; box-shadow:0 2px 6px rgba(0,0,0,.3); font-family: Arial, sans-serif; }
  </style>
</head>
<body>
  <button class="no-print pbtn" onclick="window.print()">🖨 Print / Save as PDF</button>

  <div class="letterhead">
    <img src="${logoUrl}" alt="Our Lady of Fatima" onerror="this.style.display='none'"/>
    <div class="shrine-name">OUR LADY OF FATIMA SHRINE</div>
    <div class="shrine-addr">Velachery Main Road, East Tambaram, Chennai - 600 059. &nbsp;Phone : 044-22390722</div>
    <div class="report-title">${title}</div>
    ${filterStr ? `<div class="report-filters">Filters: ${filterStr}</div>` : ''}
  </div>

  <table>
    <tr>${columns.map((c) => `<th>${c.label}</th>`).join('')}</tr>
    ${dataRows}
  </table>
  <div class="foot">
    Total records: <b>${rows.length}</b>${inactiveFootnote(rows)} &nbsp;|&nbsp; Generated: ${now}
    &nbsp;|&nbsp; Fatima Church Management System
  </div>
  <script>window.onload = function(){ window.print(); }<\/script>
</body>
</html>`;
}

// ── Multi-section report (e.g. Family Subscriptions + Cemetery Subscriptions
//    stacked in one file) ────────────────────────────────────────────────────

function buildSectionsCsv(config: ExportSectionsConfig): string {
  const { title, sections, filters } = config;
  const lines: string[] = [];

  lines.push(escapeCsv(title));
  if (filters && Object.keys(filters).length > 0) {
    const filterStr = Object.entries(filters)
      .filter(([, v]) => v)
      .map(([k, v]) => `${k}: ${v}`)
      .join(' | ');
    if (filterStr) lines.push(escapeCsv(`Filters — ${filterStr}`));
  }

  let totalRows = 0;
  sections.forEach((section) => {
    lines.push('');
    lines.push(escapeCsv(section.title));
    lines.push(section.columns.map((c) => escapeCsv(c.label)).join(','));
    section.rows.forEach((row) =>
      lines.push(section.columns.map((col) => escapeCsv(cell(col, row))).join(','))
    );
    lines.push(escapeCsv(`${section.title} — ${section.rows.length} record${section.rows.length !== 1 ? 's' : ''}`));
    totalRows += section.rows.length;
  });

  lines.push('');
  lines.push(escapeCsv(`Total records: ${totalRows}`));
  lines.push(escapeCsv(`Generated: ${new Date().toLocaleString()}`));
  lines.push(escapeCsv('Fatima Church Management System — Our Lady of Fatima, Tambaram'));

  return lines.join('\r\n');
}

function buildSectionsExcel(config: ExportSectionsConfig): string {
  const { title, sections, filters } = config;

  const filterStr = filters
    ? Object.entries(filters).filter(([, v]) => v).map(([k, v]) => `${k}: ${v}`).join(' | ')
    : '';
  const maxCols = Math.max(1, ...sections.map((s) => s.columns.length));

  const sectionBlocks = sections.map((section) => {
    const headerRow = `<tr>${section.columns.map((c) => `<td class="col-header">${c.label}</td>`).join('')}</tr>`;
    const dataRows = section.rows.map((row, i) => {
      const bg = i % 2 === 0 ? '#ffffff' : '#f5f7fa';
      const cells = section.columns.map((col) =>
        `<td style="border:1px solid #d1d5db;padding:6px 10px;background:${bg};">${cell(col, row)}</td>`
      ).join('');
      return `<tr>${cells}</tr>`;
    }).join('');
    return `
      <tr><td colspan="${maxCols}" class="section-header">${section.title}</td></tr>
      ${headerRow}
      ${dataRows}
      <tr><td colspan="${maxCols}" style="padding:4px 8px;font-size:10px;color:#6b7280;">${section.rows.length} record${section.rows.length !== 1 ? 's' : ''}</td></tr>
      <tr><td colspan="${maxCols}">&nbsp;</td></tr>
    `;
  }).join('');

  const now = new Date().toLocaleString();
  const totalRows = sections.reduce((sum, s) => sum + s.rows.length, 0);

  return `<?xml version="1.0" encoding="UTF-8"?>
<html xmlns:o="urn:schemas-microsoft-com:office:office"
      xmlns:x="urn:schemas-microsoft-com:office:excel"
      xmlns="http://www.w3.org/TR/REC-html40">
<head>
  <meta charset="UTF-8"/>
  <!--[if gte mso 9]><xml><x:ExcelWorkbook><x:ExcelWorksheets>
    <x:ExcelWorksheet><x:Name>${title}</x:Name>
    <x:WorksheetOptions><x:DisplayGridlines/></x:WorksheetOptions>
    </x:ExcelWorksheet></x:ExcelWorksheets></x:ExcelWorkbook></xml><![endif]-->
  <style>
    body  { font-family: Arial, sans-serif; font-size: 12px; }
    table { border-collapse: collapse; width: 100%; }
    .church-header { background:#1B2A4A; color:#D4AF37; font-size:18px; font-weight:bold; padding:12px 16px; }
    .sub-header    { background:#1B2A4A; color:#93c5fd; font-size:11px; padding:4px 16px 10px; }
    .section-header{ background:#153a2e; color:#ffffff; font-weight:bold; padding:8px 12px; font-size:12px; }
    .col-header    { background:#2E5C8A; color:#ffffff; font-weight:bold; padding:8px 12px;
                     border:1px solid #1B2A4A; font-size:11px; white-space:nowrap; }
    .footer        { background:#f0f4f8; color:#6b7280; font-size:10px; padding:6px 10px;
                     border-top:2px solid #2E5C8A; }
  </style>
</head>
<body>
<table>
  <tr><td colspan="${maxCols}" class="church-header">✝ ${title}</td></tr>
  <tr><td colspan="${maxCols}" class="sub-header">Our Lady of Fatima, Tambaram — Church Management System</td></tr>
  ${filterStr ? `<tr><td colspan="${maxCols}" style="background:#eff6ff;padding:4px 8px;font-size:11px;color:#1e40af;border-bottom:1px solid #bfdbfe;">Active filters: ${filterStr}</td></tr>` : ''}
  ${sectionBlocks}
  <tr>
    <td colspan="${maxCols}" class="footer">
      Total records: <b>${totalRows}</b> &nbsp;|&nbsp; Generated: ${now}
    </td>
  </tr>
</table>
</body>
</html>`;
}

function buildSectionsPdf(config: ExportSectionsConfig): string {
  const { title, sections, filters } = config;
  const now = new Date().toLocaleString();
  const logoUrl = `${window.location.origin}/images/mary-fatima.png`;

  const filterStr = filters
    ? Object.entries(filters).filter(([, v]) => v).map(([k, v]) => `${k}: ${v}`).join(' | ')
    : '';
  const totalRows = sections.reduce((sum, s) => sum + s.rows.length, 0);

  const sectionBlocks = sections.map((section, idx) => {
    const dataRows = section.rows.map((row, i) => pdfRow(section.columns, row, i)).join('');
    return `
      <div class="section-title" style="${idx > 0 ? 'page-break-before:always;' : ''}">${section.title}
        <span class="section-count">(${section.rows.length} record${section.rows.length !== 1 ? 's' : ''})</span>
      </div>
      <table>
        <tr>${section.columns.map((c) => `<th>${c.label}</th>`).join('')}</tr>
        ${dataRows}
      </table>
    `;
  }).join('');

  return `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8"/>
  <title>${title}</title>
  <style>
    @page { margin: 1.2cm; }
    @media print { .no-print { display:none !important; } }
    * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    body    { font-family: Georgia, "Times New Roman", serif; margin: 0; padding: 18px; color: #1f2937; }
    table   { border-collapse: collapse; width: 100%; margin-top: 8px; margin-bottom: 18px; }
    .letterhead   { text-align: center; padding-bottom: 10px; border-bottom: 2px solid #1B2A4A; margin-bottom: 14px; }
    .letterhead img { height: 64px; margin-bottom: 6px; }
    .shrine-name  { font-size: 19px; font-weight: bold; letter-spacing: 0.5px; color: #1B2A4A; }
    .shrine-addr  { font-size: 11px; color: #4b5563; margin-top: 3px; }
    .report-title { font-size: 15px; font-weight: bold; color: #1B2A4A; margin-top: 10px; }
    .report-filters { font-size: 10.5px; color: #6b7280; margin-top: 2px; font-weight: normal; }
    .section-title  { font-size: 13px; font-weight: bold; color: #153a2e; margin-top: 14px;
                       border-bottom: 1px solid #153a2e; padding-bottom: 4px; font-family: Arial, sans-serif; }
    .section-count  { font-size: 11px; font-weight: normal; color: #6b7280; }
    th      { background:#2E5C8A; color:#fff; padding:7px 10px; border:1px solid #1B2A4A; font-size:11px; text-align:left; white-space:nowrap; font-family: Arial, sans-serif; }
    td      { font-family: Arial, sans-serif; }
    .foot   { margin-top:10px; font-size:10px; color:#6b7280; border-top:2px solid #2E5C8A; padding-top:6px; font-family: Arial, sans-serif; }
    .pbtn   { position:fixed; top:12px; right:12px; background:#2E5C8A; color:#fff; border:none;
              padding:8px 18px; cursor:pointer; font-size:13px; border-radius:4px; box-shadow:0 2px 6px rgba(0,0,0,.3); font-family: Arial, sans-serif; }
  </style>
</head>
<body>
  <button class="no-print pbtn" onclick="window.print()">🖨 Print / Save as PDF</button>

  <div class="letterhead">
    <img src="${logoUrl}" alt="Our Lady of Fatima" onerror="this.style.display='none'"/>
    <div class="shrine-name">OUR LADY OF FATIMA SHRINE</div>
    <div class="shrine-addr">Velachery Main Road, East Tambaram, Chennai - 600 059. &nbsp;Phone : 044-22390722</div>
    <div class="report-title">${title}</div>
    ${filterStr ? `<div class="report-filters">Filters: ${filterStr}</div>` : ''}
  </div>

  ${sectionBlocks}

  <div class="foot">
    Total records: <b>${totalRows}</b>${inactiveFootnote(sections.flatMap((s) => s.rows))} &nbsp;|&nbsp; Generated: ${now}
    &nbsp;|&nbsp; Fatima Church Management System
  </div>
  <script>window.onload = function(){ window.print(); }<\/script>
</body>
</html>`;
}

// ── Download trigger ──────────────────────────────────────────────────────────

function download(content: string, filename: string, mimeType: string, bom = false) {
  const bytes = bom
    ? new Uint8Array([0xEF, 0xBB, 0xBF, ...new TextEncoder().encode(content)])
    : new TextEncoder().encode(content);
  const blob = new Blob([bytes], { type: mimeType });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href     = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// ── Public API ────────────────────────────────────────────────────────────────

export function exportData(config: ExportConfig, format: ExportFormat) {
  const tabular = { ...config, columns: withStatusColumn(config.columns, config.rows) };
  if (format === 'csv') {
    download(buildCsv(tabular), `${config.filename}.csv`, 'text/csv', /* bom */ true);
  } else if (format === 'excel') {
    download(buildExcel(tabular), `${config.filename}.xls`, 'application/vnd.ms-excel');
  } else {
    // PDF: open styled HTML in new window; browser print dialog handles save-as-PDF
    const html = buildPdf(config);
    const win  = window.open('', '_blank');
    if (win) {
      win.document.write(html);
      win.document.close();
    }
  }
}

/** Exports a report made of multiple titled sections (tables) stacked in one file. */
export function exportSections(config: ExportSectionsConfig, format: ExportFormat) {
  const tabular = {
    ...config,
    sections: config.sections.map((s) => ({ ...s, columns: withStatusColumn(s.columns, s.rows) })),
  };
  if (format === 'csv') {
    download(buildSectionsCsv(tabular), `${config.filename}.csv`, 'text/csv', /* bom */ true);
  } else if (format === 'excel') {
    download(buildSectionsExcel(tabular), `${config.filename}.xls`, 'application/vnd.ms-excel');
  } else {
    const html = buildSectionsPdf(config);
    const win  = window.open('', '_blank');
    if (win) {
      win.document.write(html);
      win.document.close();
    }
  }
}

/** Convenience date formatter for use in column `format` functions. */
export { fmtDate as exportFmtDate };
